import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { postRequest } from '../../Fetch/request';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  OutlinedInput
} from '@mui/material';

export default function CreateProblemDialog({ open, onClose, onProblemCreated, problemType = 'LLD' }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [difficultyLevel, setDifficultyLevel] = useState('Medium');
  const [companyTags, setCompanyTags] = useState([]);
  const [companyTagInput, setCompanyTagInput] = useState('');
  const [features, setFeatures] = useState([]);
  const [featureInput, setFeatureInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';

  const handleAddCompanyTag = () => {
    if (companyTagInput.trim() && !companyTags.includes(companyTagInput.trim())) {
      setCompanyTags([...companyTags, companyTagInput.trim()]);
      setCompanyTagInput('');
    }
  };

  const handleRemoveCompanyTag = (tagToRemove) => {
    setCompanyTags(companyTags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCompanyTag();
    }
  };

  const handleAddFeature = () => {
    if (featureInput.trim() && !features.includes(featureInput.trim())) {
      setFeatures([...features, featureInput.trim()]);
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (featureToRemove) => {
    setFeatures(features.filter(feature => feature !== featureToRemove));
  };

  const handleFeatureKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFeature();
    }
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      setError('Title and description are required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const problemId = uuidv4();
      const payload = {
        userId,
        problemId,
        title: title.trim(),
        description: description.trim(),
        difficultyLevel,
        companyTags,
        isDeleted: false
      };

      // Add problemDetails with features if any features are provided
      if (features.length > 0) {
        payload.problemDetails = {
          features: features
        };
      }

      const endpoint = problemType === 'HLD' 
        ? '/hld-problems/upsert' 
        : '/lld-problems/upsert';

      const response = await postRequest(
        process.env.REACT_APP_URL + endpoint,
        payload
      );

      if (response?.status === '200' || response?.status === '201') {
        // Close dialog and redirect to problem details page
        onClose();
        onProblemCreated();
        
        // Navigate to the problem details page
        const route = problemType === 'HLD' ? '/hld/problem' : '/lld/problem';
        navigate(`${route}?problemId=${problemId}&workspace=true`);
      } else {
        setError(response?.message || 'Failed to create problem');
      }
    } catch (error) {
      setError('Failed to create problem. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setDifficultyLevel('Medium');
    setCompanyTags([]);
    setCompanyTagInput('');
    setFeatures([]);
    setFeatureInput('');
    setError('');
    onClose();
  };

  const getProblemTypeLabel = () => {
    return problemType === 'HLD' ? 'High Level Design' : 'Low Level Design';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#008080' }}>
          Create New {getProblemTypeLabel()} Problem
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Problem Title"
            type="text"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Enter problem title..."
          />
          
          <TextField
            margin="dense"
            label="Problem Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Enter problem description..."
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Difficulty Level</InputLabel>
            <Select
              value={difficultyLevel}
              label="Difficulty Level"
              onChange={(e) => setDifficultyLevel(e.target.value)}
            >
              <MenuItem value="Easy">Easy</MenuItem>
              <MenuItem value="Medium">Medium</MenuItem>
              <MenuItem value="Hard">Hard</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Company Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Add Company Tag"
                value={companyTagInput}
                onChange={(e) => setCompanyTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter company name and press Enter"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddCompanyTag}
                disabled={!companyTagInput.trim()}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Add
              </Button>
            </Box>
            {companyTags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {companyTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveCompanyTag(tag)}
                    size="small"
                    sx={{ backgroundColor: '#e3f2fd' }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Features
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Add Feature"
                value={featureInput}
                onChange={(e) => setFeatureInput(e.target.value)}
                onKeyPress={handleFeatureKeyPress}
                placeholder="Enter feature and press Enter"
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="outlined"
                onClick={handleAddFeature}
                disabled={!featureInput.trim()}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Add
              </Button>
            </Box>
            {features.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {features.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    onDelete={() => handleRemoveFeature(feature)}
                    size="small"
                    sx={{ backgroundColor: '#f3e5f5' }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          sx={{ color: '#666' }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !title.trim() || !description.trim()}
          sx={{
            backgroundColor: '#008080',
            '&:hover': { backgroundColor: '#006666' },
            '&:disabled': { backgroundColor: '#ccc' }
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Creating...
            </Box>
          ) : (
            'Create Problem'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
